import React, { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';

/**
 * Cloudflare Turnstile widget (Managed mode).
 *
 * Renders explicitly via the global `window.turnstile` API loaded from:
 *   https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit
 *
 * Props:
 *   - siteKey  : string  — your public Turnstile site key (REACT_APP_TURNSTILE_SITE_KEY)
 *   - action   : string? — optional action label ("contact_form") used for analytics
 *   - onVerify : (token: string) => void — called when the user passes the challenge
 *   - onError  : () => void
 *   - onExpire : () => void
 *
 * Imperative API (via ref):
 *   turnstileRef.current.reset()  — reset & re-render the challenge
 */
const Turnstile = forwardRef(({ siteKey, action = 'contact_form', onVerify, onError, onExpire }, ref) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [scriptReady, setScriptReady] = useState(false);
  const [widgetFailed, setWidgetFailed] = useState(false);

  // Load the Turnstile script if it wasn't injected via index.html (e.g. in tests)
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return; // SSR/react-snap guard
    if (window.turnstile) {
      setScriptReady(true);
      return;
    }

    const existingScript = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => setScriptReady(true));
      existingScript.addEventListener('error', () => setWidgetFailed(true));
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.onload = () => setScriptReady(true);
    script.onerror = () => setWidgetFailed(true);
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (!scriptReady || !siteKey || widgetIdRef.current) return;
    const container = containerRef.current;
    if (!container || !window.turnstile) return;

    widgetIdRef.current = window.turnstile.render(container, {
      sitekey: siteKey,
      theme: 'auto',           // automatically matches light/dark site theme
      action: action || undefined,
      callback: (token) => onVerify?.(token),
      'error-callback': () => onError?.(),
      'expired-callback': () => onExpire?.(),
    });
  }, [scriptReady, siteKey, action, onVerify, onError, onExpire]);

  // Expose reset() to parent via ref
  useImperativeHandle(ref, () => ({
    reset() {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      } else {
        // Not rendered yet — force re-render once script is ready
        setScriptReady(true);
      }
    },
  }));

  if (!siteKey) {
    return (
      <div className="text-sm text-red-600 bg-red-50 border border-red-300 rounded-md p-3">
        Turnstile is not configured. Please set the REACT_APP_TURNSTILE_SITE_KEY environment variable.
      </div>
    );
  }

  if (widgetFailed) {
    return (
      <div className="text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded-md p-3">
        Unable to load the security check. Please try again or contact us directly.
      </div>
    );
  }

  return <div ref={containerRef} className="turnstile-container min-h-[65px]" />;
});

Turnstile.displayName = 'Turnstile';

export default Turnstile;